import i18n from '@/i18n/index.js'
import { useUi } from '@/stores/ui.js'
import { useSession } from '@/stores/session.js'
import { reportError } from '@/composables/useSave.js'

/**
 * Done in a few seconds, unless taken back (D-299): a click or a key that acts at once — putting
 * off from the work list, accepting or dismissing an AI's suggestion — waits the seconds the
 * person's account says (`undo_seconds`, UNDO_SECONDS unless set; 0: none, it acts at once) before
 * it is sent, with a toast saying what and offering "Undo". The screen shows it done already;
 * taken back, `undo` puts the screen back and nothing was sent. Leaving the page first sends
 * what is waiting at once (and the browser asks to stay a moment).
 */
export const UNDO_SECONDS = 6

const waiting = new Set()

export function later(message, run, { undo = () => {}, seconds = null } = {}) {
  const ui = useUi()
  seconds ??= useSession().me?.settings?.undo_seconds ?? UNDO_SECONDS
  if (!(seconds > 0)) {
    ui.success(message)
    return Promise.resolve(run()).catch(reportError)
  }
  const entry = {}
  entry.send = async () => {
    if (!waiting.delete(entry)) return
    clearTimeout(entry.timer)
    ui.dismiss(entry.toast)
    try {
      await run()
    } catch (e) {
      reportError(e)
    }
  }
  entry.timer = setTimeout(entry.send, seconds * 1000)
  waiting.add(entry)
  entry.toast = ui.toast(message, {
    kind: 'success',
    timeout: seconds * 1000,
    action: {
      label: i18n.global.t('common.undo'),
      run: () => {
        if (!waiting.delete(entry)) return
        clearTimeout(entry.timer)
        undo()
      },
    },
  })
  return entry
}

/** Whatever waits, sent now. */
export function sendWaiting() {
  for (const entry of [...waiting]) entry.send()
}

/** How many wait. */
export const waitingCount = () => waiting.size

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (!waiting.size) return
    sendWaiting()
    event.preventDefault()
    event.returnValue = ''
  })
}
